import React, { useEffect, useMemo, useState } from 'react';
import { Id, toast } from 'react-toastify';
import { HiXMark } from 'react-icons/hi2';
import { BiRefresh } from 'react-icons/bi';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { crmTypeToTitleCase } from '../../../utils/stringUtils';
import {
  CRMAuthStatus,
  CRMConnnectionMechanism,
  CRMIntegration,
  CrmMapping,
  CrmUser,
} from '../../../types/CRMIntegration';
import Button from '../buttons/Button';
import AuthParamsModal from '../modals/integrations/AuthParamsModal';
import ModalLayout from '../layouts/ModalLayout';
import PageHeader from '../misc/PageHeader';
import crmService from '../../../services/business/crmService';

export default function ConfigureIntegrationsPanel(): JSX.Element {
  const location = useLocation();
  const navigate = useNavigate();
  const { integration } = location.state as { integration: CRMIntegration };
  const [openAuthModals, setOpenAuthModals] = useState<{ [key: string]: boolean }>({});
  const [crmConnections, setCrmConnections] = useState<CrmUser[]>([]);
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [crmMappings, setCrmMappings] = useState<CrmMapping[]>([]);
  const { t } = useTranslation('', { keyPrefix: 'page.crm' });

  const { totalImported, importedWithVera } = useMemo(() => {
    const connections = Array.isArray(crmConnections) ? crmConnections : [];
    const mappings = Array.isArray(crmMappings) ? crmMappings : [];

    const mappedIds = new Set(mappings.map((m) => +m.crmUserId));

    const importedVeraCount = connections.reduce((count, connection) => {
      if (mappedIds.has(connection.connectionId)) {
        return count + 1;
      }
      return count;
    }, 0);

    return {
      totalImported: connections.length,
      importedWithVera: importedVeraCount,
    };
  }, [crmConnections, crmMappings]);

  const handleDelete = (crmIntegration: CRMIntegration): void => {
    const loading: Id = toast.loading(t('disconnecting', { crm: crmTypeToTitleCase(crmIntegration.crm) }));
    crmService
      .disconnectFromCrm(crmIntegration)
      .then((): void => {
        toast.success(t('disconnected', { crm: crmTypeToTitleCase(crmIntegration.crm) }));
        navigate('/integrations');
      })
      .catch((): void => {
        toast.error(t('errorDisconnecting', { crm: crmTypeToTitleCase(crmIntegration.crm) }));
      })
      .finally((): void => toast.dismiss(loading));
  };

  const handleAdd = (crmIntegration: CRMIntegration): void => {
    if (crmIntegration.connectionMechanism === CRMConnnectionMechanism.MANUAL) {
      setOpenAuthModals({ [crmIntegration.crm]: true });
    } else {
      const loading: Id = toast.loading(t('connecting', { crm: crmTypeToTitleCase(crmIntegration.crm) }));
      crmService
        .connectToCrm(crmIntegration)
        .then((): void => {
          toast.success(t('connected', { crm: crmTypeToTitleCase(crmIntegration.crm) }));
        })
        .finally((): void => toast.dismiss(loading));
    }
  };

  const handleAuthSubmitted = (crmIntegration: CRMIntegration, auth: unknown): void => {
    if (crmIntegration.connected) {
      const loading: Id = toast.loading(
        t('updatingAuth', { crm: crmTypeToTitleCase(crmIntegration.crm) }),
      );
      crmService
        .updateCrmIntegration(crmIntegration, { auth })
        .then((): void => {
          toast.success(t('updatedAuth', { crm: crmTypeToTitleCase(crmIntegration.crm) }));

          setOpenAuthModals({ [crmIntegration.crm]: false });
        })
        .finally((): void => toast.dismiss(loading));
    } else {
      const loading: Id = toast.loading(t('connecting', { crm: crmTypeToTitleCase(crmIntegration.crm) }));
      crmService
        .connectToCrm(crmIntegration, auth)
        .then((): void => {
          toast.success(t('connected', { crm: crmTypeToTitleCase(crmIntegration.crm) }));
          setOpenAuthModals({ [crmIntegration.crm]: false });
        })
        .finally((): void => toast.dismiss(loading));
    }
  };

  useEffect(() => {
    crmService
      .getUsers(integration)
      .then(setCrmConnections);

    crmService
      .getCrmMappings(integration)
      .then((mappings) => {
        setCrmMappings(Array.isArray(mappings) ? mappings : []);
      });
  }, []);

  if (!integration) return <></>;

  return (
    <>
      <PageHeader
        title={t('manageConnection', { crm: crmTypeToTitleCase(integration.crm) })}
        subtitle={t('integrate', { crm: crmTypeToTitleCase(integration.crm) })}
      />
      <div className="flex flex-col gap-6 pt-2 lg:flex-row">
        {/* Connection card */}
        <section className="flex-1 rounded-[12px] border border-secondary-200 bg-secondary-50 px-6 py-5 shadow-sm">
          <div className="mb-4 flex items-start justify-between gap-4">
            <div>
              <h2 className="text-lg font-semibold">
                {t('connectionCard.title', { crm: crmTypeToTitleCase(integration.crm) })}
              </h2>
              <p className="mt-1 text-sm text-gray-600">
                {t('connectionCard.subtitle')}
              </p>
            </div>
            {integration.connected ? (
              <span className="rounded-full bg-success-100 px-3 py-1 text-xs font-semibold text-success-700">
                {integration.authStatus === CRMAuthStatus.INVALID
                  ? t('status.attention')
                  : t('status.connected')}
              </span>
            ) : (
              <span className="rounded-full bg-secondary-200 px-3 py-1 text-xs font-semibold text-gray-700">
                {t('status.notConnected')}
              </span>
            )}
          </div>
          {integration.connected && integration.authStatus === CRMAuthStatus.INVALID && (
            <div className="mb-4 rounded-[12px] bg-warning-100 px-4 py-3 text-sm text-warning-900">
              {t('connectionCard.invalidAuth')}
            </div>
          )}
          <div className="flex flex-wrap items-center gap-3">
            {integration.connectionMechanism === CRMConnnectionMechanism.MANUAL && (
              <Button
                variant="primary"
                onClick={(): void => handleAdd(integration)}
                className="flex items-center gap-2"
              >
                <BiRefresh className="h-5 w-5" />
                {integration.connected ? t('connectionCard.updateManual') : t('connectionCard.connectManual')}
              </Button>
            )}
            {integration.connectionMechanism !== CRMConnnectionMechanism.MANUAL && !integration.connected && (
              <Button
                variant="primary"
                onClick={(): void => handleAdd(integration)}
                className="flex items-center gap-2"
              >
                {t('connectionCard.connect', { crm: crmTypeToTitleCase(integration.crm) })}
              </Button>
            )}
            {integration.connected && (
              <Button
                variant="secondary"
                onClick={(): void => handleDelete(integration)}
                className="flex items-center gap-2"
              >
                <HiXMark className="h-5 w-5" />
                {t('disconnect')}
              </Button>
            )}
          </div>
          {integration.connectionMechanism === CRMConnnectionMechanism.MANUAL && (
            <AuthParamsModal
              crmIntegration={integration}
              open={openAuthModals[integration.crm] ?? false}
              setOpen={(open) => setOpenAuthModals({ [integration.crm]: open })}
              onSubmitted={(auth) => handleAuthSubmitted(integration, auth)}
            />
          )}
        </section>

        {/* People & access card */}
        <section className="flex-1 rounded-[12px] border border-secondary-200 bg-secondary-50 px-6 py-5 shadow-sm">
          <div className="mb-4">
            <h2 className="text-lg font-semibold">{t('peopleCard.title')}</h2>
            <p className="mt-1 text-sm text-gray-600">
              {t('peopleCard.subtitle')}
            </p>
          </div>
          <Button
            variant="primary"
            className="mt-auto flex items-center gap-2"
            onClick={() => setInviteModalOpen(true)}
            disabled={!integration.connected}
          >
            {t('peopleCard.cta')}
          </Button>
          {!integration.connected && (
            <p className="mt-2 text-xs text-gray-500">
              {t('peopleCard.requiresConnection')}
            </p>
          )}
        </section>
      </div>

      {/* Data sync / mappings card */}
      <section className="mt-6 rounded-[12px] border border-secondary-200 bg-secondary-50 px-6 py-5 shadow-sm">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-4">
          <div>
            <h2 className="text-lg font-semibold">{t('mappingsCard.title')}</h2>
            <p className="mt-1 text-sm text-gray-600">
              {t('mappingsCard.subtitle')}
            </p>
          </div>
        </div>
        <div className="mb-4 flex flex-wrap gap-4">
          <div className="rounded-[12px] bg-secondary-100 px-4 py-3">
            <p className="text-xs font-semibold uppercase text-gray-500">
              {t('mappingsCard.externalLabel')}
            </p>
            <p className="text-lg font-semibold">
              {t('mappingsCard.externalSummary', {
                count: totalImported,
              })}
            </p>
          </div>
          <div className="rounded-[12px] bg-secondary-100 px-4 py-3">
            <p className="text-xs font-semibold uppercase text-gray-500">
              {t('mappingsCard.mappedLabel')}
            </p>
            <p className="text-lg font-semibold">
              {t('mappingsCard.mappedSummary', {
                count: importedWithVera,
              })}
            </p>
          </div>
        </div>

        <div className="mt-2">
          <p className="mb-2 text-sm font-semibold text-gray-700">
            {t('mappingsCard.listTitle')}
          </p>
          {crmConnections.length === 0 ? (
            <p className="text-sm text-gray-500">
              {t('mappingsCard.empty')}
            </p>
          ) : (
            <div className="no-scrollbar max-h-64 overflow-y-auto rounded-[12px] border border-secondary-200 bg-white">
              <div className="grid grid-cols-3 gap-2 border-b border-secondary-200 px-4 py-2 text-xs font-semibold text-gray-500">
                <span>{t('mappingsCard.nameHeader')}</span>
                <span>{t('mappingsCard.emailHeader')}</span>
                <span>{t('mappingsCard.statusHeader')}</span>
              </div>
              {crmConnections.map((c) => {
                const mappings = Array.isArray(crmMappings) ? crmMappings : [];
                // crmUserId refers to the external CRM user; connectionId is the CRM user's id here
                const isMapped = mappings.some((m) => +m.crmUserId === c.connectionId);

                return (
                  <div
                    key={c.connectionId}
                    className="grid grid-cols-3 items-center gap-2 border-b border-secondary-100 px-4 py-2 text-sm last:border-b-0"
                  >
                    <span className="truncate">{c.name ?? c.email ?? '-'}</span>
                    <span className="truncate text-gray-600">{c.email ?? '-'}</span>
                    <span
                      className={`inline-flex w-fit rounded-full px-2 py-0.5 text-xs font-semibold ${
                        isMapped
                          ? 'bg-success-100 text-success-800'
                          : 'bg-secondary-100 text-gray-700'
                      }`}
                    >
                      {isMapped ? t('mappingsCard.hasVera') : t('mappingsCard.noVera')}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>
      <ModalLayout
        open={inviteModalOpen}
        setOpen={setInviteModalOpen}
        className="h-a z-50 mx-6 flex min-w-[450px] max-w-[600px] flex-col rounded-[12px] bg-secondary-50 px-10 py-6 pb-4 pt-5">
        <div className="mb-4">
          <h2 className="text-xl font-semibold">{t('inviteInfoModal.title')}</h2>
          <p className="mt-1 text-sm text-gray-600">
            {t('inviteInfoModal.subtitle')}
          </p>
        </div>
        <div className="mt-2 space-y-3 text-sm text-gray-700">
          <p>{t('inviteInfoModal.body1')}</p>
          <p>{t('inviteInfoModal.body2')}</p>
        </div>
        <div className="mt-6 flex w-full justify-end">
          <Button
            variant="secondary"
            onClick={() => setInviteModalOpen(false)}
          >
            {t('inviteInfoModal.close')}
          </Button>
        </div>
      </ModalLayout>
    </>
  );
}
